import React, { useState, useRef, useEffect, useCallback } from 'react';
import Grid from '@mui/material/Grid';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Skeleton from '@mui/material/Skeleton';
import Paper from '@mui/material/Paper';
import ButtonBase from '@mui/material/ButtonBase';

import { Link } from '@mui/material';

import ShowMoreContent from '../../../base/ShowMoreContent';
import { CardContainer } from './CardContainer';
import { useDetailsViewContext } from '../DetailsViewContext';
import IconText from './../../../base/IconText';

import CoinsStackIcon from '../../../base/CoinStackIcon';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import { MuiMarkdown, getOverrides } from 'mui-markdown';

/**
 * Card component displaying product information
 *
 * @param {Object} props
 * @param {number|null} props.allocatedHeight - Height allocated by parent for this card
 * @param {boolean} props.autoExpand - Whether to auto-expand and hide ShowMoreCard toggle
 * @param {boolean} props.forceCollapsed - When true, use fixed collapsed height (for mobile)
 * @param {Function} props.onDimensionsChange - Callback to report min/max dimensions
 */
export default function ProductCard({
  allocatedHeight,
  autoExpand = false,
  forceCollapsed = false,
  onDimensionsChange,
}) {
  // Fixed height for mobile collapsed state (~3-4 lines of text)
  const mobileCollapsedHeight = 80;

  const [expanded, setExpanded] = useState(autoExpand && !forceCollapsed);
  // Track if collapse animation is complete - only then apply height constraint
  const [collapseAnimationComplete, setCollapseAnimationComplete] =
    useState(true);

  const { product, openProductPage, products, noProductsAvailable } =
    useDetailsViewContext();

  // Refs for dimension measurement
  const fixedContentRef = useRef(null);
  const maxContentRef = useRef(null);

  // State for measured fixed content height (name + icons)
  const [fixedContentHeight, setFixedContentHeight] = useState(0);

  // Sync expanded state with autoExpand/forceCollapsed props
  useEffect(() => {
    if (forceCollapsed) {
      // When forceCollapsed is enabled, ensure collapsed state
      setExpanded(false);
    } else {
      // When forceCollapsed is disabled, sync with autoExpand
      setExpanded(autoExpand);
      if (autoExpand) {
        // When switching to autoExpand, ensure height constraint is released
        setCollapseAnimationComplete(true);
      }
    }
  }, [autoExpand, forceCollapsed]);

  const toggleExpansion = useCallback(() => {
    if (!autoExpand) {
      setExpanded((prev) => {
        const newExpanded = !prev;
        if (!newExpanded) {
          // Starting collapse - animation not yet complete
          setCollapseAnimationComplete(false);
        }
        return newExpanded;
      });
    }
  }, [autoExpand]);

  // Called when ShowMoreCard animation finishes.
  // Only tracks collapse animation completion since we need to defer
  // height constraint until collapse animation finishes.
  // Expand animation doesn't need tracking - height constraint is
  // removed immediately when expansion starts.
  const handleAnimationComplete = useCallback((isExpanded) => {
    if (!isExpanded) {
      // Collapse animation finished - now safe to apply height constraint
      setCollapseAnimationComplete(true);
    }
  }, []);

  // Measure fixed content height (name + icons)
  useEffect(() => {
    const measureFixedContent = () => {
      if (fixedContentRef.current) {
        setFixedContentHeight(fixedContentRef.current.scrollHeight || 0);
      }
    };

    const rafId = requestAnimationFrame(measureFixedContent);

    const ro = new ResizeObserver(measureFixedContent);
    if (fixedContentRef.current) ro.observe(fixedContentRef.current);

    return () => {
      cancelAnimationFrame(rafId);
      ro.disconnect();
    };
  }, [product]);

  // Measure and report dimensions for height distribution
  useEffect(() => {
    if (!onDimensionsChange) return;

    const fixedEl = fixedContentRef.current;
    const maxEl = maxContentRef.current;

    if (!fixedEl || !maxEl) return;

    const measureDimensions = () => {
      // Min height = fixed content only (ShowMoreCard will add to this)
      // For now, use fixed content as minimum - the ShowMoreCard component
      // will handle its own minimum display
      const minHeight = fixedEl?.scrollHeight || 0;
      const maxHeight = maxEl?.scrollHeight || 0;
      onDimensionsChange({ minHeight, maxHeight });
    };

    let rafId1;
    let rafId2;
    rafId1 = requestAnimationFrame(() => {
      rafId2 = requestAnimationFrame(measureDimensions);
    });

    const ro = new ResizeObserver(measureDimensions);
    ro.observe(fixedEl);
    ro.observe(maxEl);

    return () => {
      if (rafId1) cancelAnimationFrame(rafId1);
      if (rafId2) cancelAnimationFrame(rafId2);
      ro.disconnect();
    };
  }, [onDimensionsChange, product]);

  const renderIcons = product?.price || product?.minDuration;

  if (!product) {
    // Products query has settled with nothing — mirror ResourceCard's null
    // return rather than sit on a skeleton that will never resolve.
    if (noProductsAvailable) {
      return null;
    }
    return <ProductCardSkeleton />;
  }

  // Shared content for name (used in both measurement and actual render)
  const renderName = () =>
    openProductPage && products?.length > 1 ? (
      <Link
        underline="none"
        color="text.secondary"
        onClick={openProductPage}
        sx={{ cursor: 'pointer' }}
      >
        {product?.name}
      </Link>
    ) : (
      <Typography
        variant="body1"
        sx={{
          color: 'text.secondary',
        }}
      >
        {product?.name}
      </Typography>
    );

  // Shared icons content
  const renderIconsContent = () =>
    renderIcons && (
      <Grid
        container
        item
        xs={12}
        sx={{
          mt: 1,
        }}
      >
        {product?.minDuration && (
          <Grid item xs={12}>
            <IconText icon={AccessTimeIcon}>
              {`${product.minDuration} min`}
            </IconText>
          </Grid>
        )}
        {product?.price && (
          <Grid item xs={12}>
            <IconText icon={CoinsStackIcon}>{product.price}</IconText>
          </Grid>
        )}
      </Grid>
    );

  // Calculate maxHeight for ShowMoreCard
  // This is the remaining height after fixed content (name, icons, padding, spacing)
  // Note: We always pass maxHeight (even when expanded) so ShowMoreCard knows
  // whether to show the collapse button. ShowMoreCard handles displaying
  // full height when expanded.
  const calculateShowMoreMaxHeight = () => {
    // When forceCollapsed, use fixed mobile height regardless of other settings
    if (forceCollapsed) {
      return mobileCollapsedHeight;
    }

    if (!allocatedHeight || autoExpand) {
      return null; // No limit
    }

    // Card padding: p: { xs: 1, sm: 2 } = 8px or 16px on each side
    // Using 16px (sm) as default since we're in DesktopLayout
    const cardPadding = 32; // 16px top + 16px bottom
    const showMoreSpacing = 8; // mt: 1 = 8px spacing before ShowMoreCard

    const availableForShowMore =
      allocatedHeight - fixedContentHeight - cardPadding - showMoreSpacing;

    return Math.max(0, availableForShowMore);
  };

  const showMoreMaxHeight = calculateShowMoreMaxHeight();

  return (
    <Grid id="product-card" item container xs={12}>
      {/* Hidden measurement element for fixed content (name + icons) */}
      <Box
        ref={fixedContentRef}
        aria-hidden="true"
        sx={{
          position: 'absolute',
          left: 0,
          top: 0,
          right: 0,
          opacity: 0,
          pointerEvents: 'none',
          zIndex: -1,
        }}
      >
        <CardContainer>
          <Grid
            item
            xs={12}
            sx={{
              pt: '0 !important',
            }}
          >
            {renderName()}
          </Grid>
          {renderIconsContent()}
        </CardContainer>
      </Box>

      {/* Hidden measurement element for max content (everything expanded) */}
      <Box
        ref={maxContentRef}
        aria-hidden="true"
        sx={{
          position: 'absolute',
          left: 0,
          top: 0,
          right: 0,
          opacity: 0,
          pointerEvents: 'none',
          zIndex: -1,
        }}
      >
        <CardContainer>
          <Grid
            item
            xs={12}
            sx={{
              pt: '0 !important',
            }}
          >
            {renderName()}
          </Grid>
          {renderIconsContent()}
          {product?.detailsPre && (
            <Grid
              item
              xs={12}
              sx={{
                mt: 1,
              }}
            >
              <MuiMarkdown>{product.detailsPre}</MuiMarkdown>
            </Grid>
          )}
        </CardContainer>
      </Box>

      {/* Actual visible content */}
      <CardContainer
        sx={{
          ...(allocatedHeight &&
            !expanded &&
            collapseAnimationComplete && {
              maxHeight: `${allocatedHeight}px`,
              overflow: 'hidden',
            }),
        }}
      >
        <Grid
          item
          xs={12}
          sx={{
            pt: '0 !important',
          }}
        >
          {renderName()}
        </Grid>

        {renderIconsContent()}

        {product?.detailsPre && (
          <Grid
            item
            xs={12}
            sx={{
              mt: 1,
            }}
          >
            {autoExpand ? (
              // Full content without ShowMoreCard
              <MuiMarkdown>{product.detailsPre}</MuiMarkdown>
            ) : (
              <ShowMoreContent
                maxHeight={showMoreMaxHeight}
                expanded={expanded}
                onToggle={toggleExpansion}
                onAnimationComplete={handleAnimationComplete}
                buttonComponent={ButtonBase}
                buttonProps={{
                  buttonSx: {
                    pr: 1,
                    borderRadius: (theme) => `${theme.shape.borderRadius}px`,
                    '&:hover': {
                      backgroundColor: 'action.hover',
                    },
                  },
                }}
              >
                <MuiMarkdown
                  overrides={{
                    ...getOverrides({}),
                    a: {
                      component: Link,
                      props: {
                        target: '_blank',
                      },
                    },
                  }}
                >
                  {product.detailsPre}
                </MuiMarkdown>
              </ShowMoreContent>
            )}
          </Grid>
        )}
      </CardContainer>
    </Grid>
  );
}

/**
 * Loading skeleton for ProductCard
 */
export function ProductCardSkeleton() {
  return (
    <Paper
      sx={{
        p: 2,
        border: '1px solid',
        borderRadius: (theme) => `${theme.shape.borderRadius}px`,
        borderColor: (theme) => theme.palette.divider,
      }}
    >
      <Skeleton variant="text" width="40%" height={24} />
      <Skeleton variant="text" width="80%" height={20} />
      <Skeleton variant="text" width="60%" height={20} />
    </Paper>
  );
}
